<template>
  <div class="project-list">
    <div class="toolbar">
      <el-button type="primary" icon="el-icon-plus" @click="showCreateDialog">新建项目</el-button>
      <el-input 
        v-model="searchName" 
        class="search-input" 
        placeholder="搜索项目名称" 
        prefix-icon="el-icon-search"
        clearable
        @keyup.enter.native="handleSearch"
      />
    </div>
    
    <div class="project-cards">
      <!-- 项目卡片：点击跳转到项目详情页 -->
      <div 
        v-for="project in filteredProjects" 
        :key="project.id" 
        class="project-card"
        @click="goToProject(project)"
      >
        <div class="card-content">
          <!-- 项目名称和状态 -->
          <div class="card-header">
            <h3 class="project-name">{{ project.name }}</h3>
            <el-tag :type="project.status === 1 ? 'success' : 'info'" size="small" class="status-tag">
              {{ project.status === 1 ? '进行中' : '已完成' }}
            </el-tag>
          </div>
          
          <!-- 项目代号 -->
          <div class="project-code" v-if="project.code">
            <i class="el-icon-tickets"></i> {{ project.code }}
          </div>
          
          <!-- 项目时间 -->
          <div class="project-time" v-if="project.start_time || project.end_time">
            <i class="el-icon-date"></i>
            <span v-if="project.start_time && project.end_time">
              {{ formatDate(project.start_time) }} ~ {{ formatDate(project.end_time) }}
            </span>
            <span v-else-if="project.start_time">
              {{ formatDate(project.start_time) }} 开始
            </span>
            <span v-else>
              截止 {{ formatDate(project.end_time) }}
            </span>
          </div>
          
          <!-- 统计信息 -->
          <div class="project-stats">
            <div class="stat-item">
              <i class="el-icon-document"></i>
              <span class="stat-value">{{ project.form_count || 0 }}</span>
              <span class="stat-label">问卷</span>
            </div>
            <div class="stat-item" v-if="project.customer_name">
              <i class="el-icon-user"></i>
              <span class="stat-value">{{ project.customer_name }}</span>
            </div>
          </div>
        </div>
      </div>
      
      <!-- 新建项目卡片 -->
      <div class="project-card add-card" @click="showCreateDialog">
        <i class="el-icon-plus"></i>
        <span>新建项目</span>
      </div>
    </div>
    
    <!-- 新建/编辑项目对话框 -->
    <el-dialog 
      :title="editingProject ? '编辑项目' : '新建项目'" 
      :visible.sync="dialogVisible" 
      width="500px"
    >
      <el-form ref="form" :model="formData" :rules="rules" label-width="80px">
        <el-form-item label="项目名称" prop="name">
          <el-input v-model="formData.name" placeholder="请输入项目名称" />
        </el-form-item>
        <el-form-item label="项目代号" prop="code">
          <el-input v-model="formData.code" placeholder="请输入项目代号（如：PRJ-2026-001）" />
        </el-form-item>
        <el-form-item label="项目时间" prop="timeRange">
          <el-date-picker
            v-model="formData.timeRange"
            type="daterange"
            range-separator="至"
            start-placeholder="开始日期"
            end-placeholder="结束日期"
            value-format="yyyy-MM-dd"
            style="width: 100%"
          />
        </el-form-item>
        <el-form-item label="项目描述">
          <el-input v-model="formData.description" type="textarea" :rows="3" placeholder="请输入项目描述" />
        </el-form-item>
        <el-form-item label="项目状态">
          <el-radio-group v-model="formData.status">
            <el-radio :label="1">进行中</el-radio>
            <el-radio :label="2">已完成</el-radio>
          </el-radio-group>
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="saving" @click="handleSave">保存</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { getProjectList, addProject, updateProject } from '@/app/index/api/project/manager'

export default {
  name: 'ProjectList',
  data() {
    return {
      searchName: '',
      dialogVisible: false,
      editingProject: null,
      saving: false,
      projects: [],
      formData: {
        name: '',
        code: '',
        customer_name: '',
        timeRange: [],
        description: '',
        status: 1
      },
      rules: {
        name: [{ required: true, message: '请输入项目名称', trigger: 'blur' }]
      }
    }
  },
  computed: {
    filteredProjects() {
       return this.projects
    }
  },
  mounted() {
    this.loadProjects()
  },
  methods: {
    async loadProjects() {
      try {
        const res = await getProjectList({
          page: 1,
          pageSize: 100,
          keyword: this.searchName
        })
        // 响应结构: res.data = {success, errcode, message, data: {records, total, size}}
        const records = res.data?.data?.records || res.data?.records || []
        this.projects = records
      } catch (e) {
        // axios拦截器拒绝时数据在 e.data 中
        const records = e?.data?.data?.records || e?.data?.records
        if (records && records.length > 0) {
          this.projects = records
        } else {
          console.error('加载项目列表失败', e)
        }
      }
    },
    goToProject(project) {
      // 点击项目卡片跳转到项目详情页（概览Tab）
      this.$router.push(`/project/${project.id}/overview`)
    },
    showCreateDialog() {
      this.editingProject = null
      this.formData = { name: '', code: '', customer_name: '', timeRange: [], description: '', status: 1 }
      this.dialogVisible = true
    },
    editProject(project) {
      this.editingProject = project
      // 构建时间范围数组
      const timeRange = []
      if (project.start_time || project.startTime) {
        timeRange[0] = project.start_time || project.startTime
      }
      if (project.end_time || project.endTime) {
        timeRange[1] = project.end_time || project.endTime
      }
      this.formData = {
        ...project,
        code: project.code || project.Code || '',
        customer_name: project.customer_name || project.customerName || '',
        timeRange: timeRange.length === 2 ? timeRange : []
      }
      this.dialogVisible = true
    },
    async handleSave() {
      try {
        await this.$refs.form.validate()
        this.saving = true
        
        // 构建提交数据，将 timeRange 转换为 start_time 和 end_time
        const submitData = {
          ...this.formData,
          start_time: this.formData.timeRange && this.formData.timeRange[0] ? this.formData.timeRange[0] : null,
          end_time: this.formData.timeRange && this.formData.timeRange[1] ? this.formData.timeRange[1] : null
        }
        delete submitData.timeRange
        
        if (this.editingProject) {
            await updateProject(submitData)
        } else {
            await addProject(submitData)
        }
        this.$message.success(this.editingProject ? '项目更新成功' : '项目创建成功')
        this.dialogVisible = false
        this.loadProjects()
      } catch (e) {
        console.error(e)
      } finally {
        this.saving = false
      }
    },
    handleSearch() {
      this.loadProjects()
    },
    formatDate(dateStr) {
      if (!dateStr) return ''
      const d = new Date(dateStr)
      const y = d.getFullYear()
      const m = String(d.getMonth() + 1).padStart(2, '0')
      const day = String(d.getDate()).padStart(2, '0')
      return `${y}-${m}-${day}`
    }
  }
}
</script>

<style scoped>
.project-list {
  padding: 20px;
}

.toolbar {
  display: flex;
  justify-content: space-between;
  margin-bottom: 20px;
}

.search-input {
  width: 300px;
}

.project-cards {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 20px;
}

.project-card {
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
  overflow: hidden;
  transition: all 0.3s ease;
  cursor: pointer;
  border: 1px solid #f0f0f0;
}

.project-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 24px rgba(64, 158, 255, 0.15);
  border-color: #409eff;
}

.card-content {
  padding: 20px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 12px;
}

.project-name {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #303133;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
  margin-right: 12px;
}

.status-tag {
  flex-shrink: 0;
}

.project-code {
  font-size: 13px;
  color: #606266;
  margin-bottom: 8px;
}

.project-code i {
  color: #909399;
  margin-right: 4px;
}

.project-time {
  font-size: 13px;
  color: #909399;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  gap: 6px;
}

.project-time i {
  color: #c0c4cc;
}

.project-stats {
  display: flex;
  gap: 20px;
  padding-top: 12px;
  border-top: 1px solid #f5f5f5;
}

.stat-item {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 13px;
  color: #606266;
}

.stat-item i {
  font-size: 14px;
  color: #909399;
}

.stat-value {
  font-weight: 600;
  color: #409eff;
}

.stat-label {
  color: #909399;
}

.add-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 140px;
  border: 2px dashed #dcdfe6;
  background: #fafbfc;
  color: #909399;
  transition: all 0.3s;
}

.add-card:hover {
  border-color: #409eff;
  color: #409eff;
  background: #f0f7ff;
}

.add-card i {
  font-size: 32px;
  margin-bottom: 8px;
}
</style>
